import { ArrowLeft, ArrowRight, Tick, TimeClock } from '@berlitz/icons'
import Locked from '@berlitz/streamline-icons/lib/bold/01-Interface-Essential/11-Lock-Unlock/Lock2'
import Unlocked from '@berlitz/streamline-icons/lib/bold/01-Interface-Essential/11-Lock-Unlock/LockUnlock1'
import { LP_STATUS } from '@utils/constants/learningPath'
import styled, { css } from 'styled-components'
import { Active, Disabled, Status } from './interface'

export const Container = styled.div`
  border-top: 1px solid ${({ theme }) => theme.palette.gray100};
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.space.md};

  &.no-navs {
    padding-top: ${({ theme }) => theme.space.xs};
    padding-bottom: ${({ theme }) => theme.space.xs};
  }
`

const ArrowCSS = css`
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  cursor: pointer;
  height: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.palette.blue80};

    svg {
      color: ${({ theme }) => theme.colors.brandPrimaryContrast};
    }
  }
`

const ArrowDisable = css`
  background-color: ${({ theme }) => theme.colors.ui02};
  cursor: not-allowed;
  svg {
    color: ${({ theme }) => theme.palette.gray100};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.ui02};
    svg {
      color: ${({ theme }) => theme.palette.gray100};
    }
  }
`

export const StyledArrowLeft = styled(ArrowLeft)<Disabled>`
  ${ArrowCSS};
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.xs} ${theme.space.sm}`};

  ${({ disabled }) => disabled && ArrowDisable}
`

export const StyledArrowRight = styled(ArrowRight)<Disabled>`
  ${ArrowCSS}
  svg {
    font-size: ${({ theme }) => theme.fontSize.xxxl};
  }

  ${({ disabled }) => disabled && ArrowDisable}
`

export const RightHolder = styled.div`
  &.full-width {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`

export const DotItem = styled.div`
  flex: auto;
  width: 100%;
  justify-content: center;
  align-self: stretch;
  display: flex;
  align-items: center;
  position: relative;
  padding-top: ${({ theme }) => theme.space.xxs};
  padding-bottom: ${({ theme }) => theme.space.xxs};
`

export const DotContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
`

export const Line = styled.div<Status>`
  width: 50%;

  &.pending {
    border: 1px dashed ${({ theme }) => theme.palette.blueGray30};
    position: absolute;
    width: 100%;
  }

  &.clear {
    border: 1px solid ${({ theme }) => theme.colors.brandPrimaryContrast};
  }

  ${({ status }) =>
    status &&
    css`
      ${status === 'inprogress' &&
        css`
          border: 1px solid ${({ theme }) => theme.palette.purple70};
        `}

      ${status === 'complete' &&
        css`
          border: 1px solid ${({ theme }) => theme.palette.green40};
        `}
    `}
`

const DotHover = css`
  width: 22px;
  height: 22px;
  background-color: ${({ theme }) => theme.palette.blueGray50};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
  margin: ${({ theme }) => theme.space.xxxs};
`

export const Dot = styled.div<Active & Status>`
  width: ${({ theme }) => theme.layout.xxs};
  height: ${({ theme }) => theme.layout.xxs};
  border-radius: ${({ theme }) => theme.layout.xxxl};
  background-color: ${({ theme }) => theme.palette.gray60};
  cursor: pointer;
  position: relative;
  z-index: 1;

  &:hover {
    ${DotHover}
  }

  ${({ active }) =>
    active &&
    css`
    ${DotHover}
    background-color: ${({ theme }) => theme.colors.brandPrimary};
    &:hover {
      background-color: ${({ theme }) => theme.colors.brandPrimary};
    }
  `}

  ${({ status }) =>
    status === LP_STATUS.COURSE_META &&
    css`
    ${DotHover}
    background-color: ${({ theme }) => theme.palette.warning100};
    &:hover {
      background-color: ${({ theme }) => theme.palette.warning100};
    }
  `}
`

const StatusShadow = css`
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
`
const StatusCSS = css`
  width: ${({ theme }) => theme.layout.xs};
  height: ${({ theme }) => theme.layout.xs};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${({ theme }) => theme.layout.xxl};
  cursor: pointer;
  margin: 1px;
  padding: ${({ theme }) => theme.space.xxs};
  position: relative;
  z-index: 1;

  svg {
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
`

export const IconHolder = styled.div<Status & Active>`
  border-radius: ${({ theme }) => theme.layout.xxxl};
  &:hover {
    ${StatusShadow}
  }

  ${({ status, active }) =>
    active &&
    css`
      ${StatusShadow};
      background-color: ${({ theme }) => theme.colors.brandSupporting01Contrast};
      border: 2px solid ${({ theme }) => theme.palette.blue60};

      ${status === LP_STATUS.IN_PROGRESS &&
        css`
          border: 2px solid ${({ theme }) => theme.palette.purple70};
        `}

      ${status === LP_STATUS.COMPLETE &&
        css`
          border: 2px solid ${({ theme }) => theme.palette.green40};
        `}
    `}
`

export const StyledTick = styled(Tick)`
  ${StatusCSS};
  background-color: ${({ theme }) => theme.colors.brandSecondary};
  color: ${({ theme }) => theme.colors.brandSecondaryContrast};
`

const LockCss = css<Active>`
  ${StatusCSS};
  background-color: ${({ theme }) => theme.palette.gray60};
  color: ${({ theme }) => theme.colors.brandPrimaryContrast};
  svg {
    font-size: ${({ theme }) => theme.fontSize.xxs};
  }

  ${({ active }) =>
    active &&
    css`
      background-color: ${({ theme }) => theme.colors.brandPrimary};
    `}
`

export const StyledLocked = styled(Locked)<Active>`
  ${LockCss};
`

export const StyledUnlocked = styled(Unlocked)<Active>`
  ${LockCss};
`

export const StyledTimeClock = styled(TimeClock)`
  ${StatusCSS}
  background-color: ${({ theme }) => theme.colors.ui08};
  color: ${({ theme }) => theme.colors.brandSupporting01Contrast};
`
export const EmptyNavPlaceholder = styled.div`
  height: ${({ theme }) => theme.space.xxl};
`
